import { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector, useDispatch } from 'react-redux'
import { Fade, Slide, Typography, Paper, Box, Divider } from '@material-ui/core'
import { useQuery } from '@apollo/react-hooks'
import ChatSearchInput from './ChatSearchInput'
import MessageBox from './MessageBox'
import ChatTabs from './ChatTabs'
import ChatList from './ChatList'
import BuddyListWithPresence from '../BuddyList/BuddyListWithPresence'
import UserSearchResults from './UserSearchResults'
import { Button, IconButton, Tooltip, Avatar } from '@material-ui/core'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import SettingsIcon from '@material-ui/icons/Settings'
import { usePresenceHeartbeat } from '../../hooks/usePresenceHeartbeat'
import StatusEditor from './StatusEditor'
import { SET_STATUS_EDITOR_OPEN } from '../../store/chat'
import { GET_CHAT_ROOMS } from '../../graphql/query'
import PresenceIcon from './PresenceIcon'

const useStyles = makeStyles((theme) => ({
  root: {
    width: 400,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #1e1e1e 0%, #121212 100%)'
      : 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? '2px 0 12px rgba(0, 0, 0, 0.3), 1px 0 4px rgba(0, 0, 0, 0.2)'
      : '2px 0 12px rgba(0, 0, 0, 0.04), 1px 0 4px rgba(0, 0, 0, 0.02)',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      borderRight: 'none',
    },
  },
  sidebarHeader: {
    padding: theme.spacing(2.5, 2.5, 2, 2.5),
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #2A3F35 0%, #1F3428 100%)'
      : 'linear-gradient(135deg, #52b274 0%, #4a9e63 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(82, 178, 116, 0.08) 0%, rgba(74, 158, 99, 0.05) 100%)'
        : 'linear-gradient(135deg, rgba(82, 178, 116, 0.1) 0%, rgba(74, 158, 99, 0.1) 100%)',
      opacity: 0.5,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -50,
      right: -50,
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: theme.palette.mode === 'dark'
        ? 'rgba(82, 178, 116, 0.08)'
        : 'rgba(255, 255, 255, 0.1)',
      filter: 'blur(40px)',
    },
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
  },
  sidebarTitle: {
    fontSize: '1.75rem',
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: theme.spacing(0.75),
    letterSpacing: '-0.02em',
    textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  userStatusCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    background: theme.palette.mode === 'dark'
      ? 'rgba(82, 178, 116, 0.15)'
      : 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(82, 178, 116, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.3)',
    marginTop: theme.spacing(0.5),
  },
  userStatusText: {
    fontSize: '0.8125rem',
    color: theme.palette.mode === 'dark' ? '#EDEDED' : 'rgba(255, 255, 255, 0.95)',
    fontWeight: 500,
  },
  settingsButton: {
    color: '#ffffff',
    background: theme.palette.mode === 'dark'
      ? 'rgba(82, 178, 116, 0.15)'
      : 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(82, 178, 116, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.3)',
    padding: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
      background: theme.palette.mode === 'dark'
        ? 'rgba(82, 178, 116, 0.25)'
        : 'rgba(255, 255, 255, 0.3)',
      transform: 'scale(1.05)',
    },
  },
  searchContainer: {
    padding: theme.spacing(2, 2.5),
    background: theme.palette.mode === 'dark' ? '#1F1F1F' : '#ffffff',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 4px rgba(0, 0, 0, 0.2)'
      : '0 2px 4px rgba(0, 0, 0, 0.02)',
  },
  tabsContainer: {
    background: theme.palette.mode === 'dark' ? '#1F1F1F' : '#ffffff',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 4px rgba(0, 0, 0, 0.2)'
      : '0 2px 4px rgba(0, 0, 0, 0.02)',
  },
  contentArea: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(to bottom, #1A1A1A 0%, #141414 50%, #1A1A1A 100%)'
      : 'linear-gradient(to bottom, #fafbfc 0%, #ffffff 50%, #f5f7fa 100%)',
    position: 'relative',
  },
  addBuddyContainer: {
    padding: theme.spacing(2, 2.5),
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #1F1F1F 0%, #1A1A1A 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.2)'
      : '0 2px 8px rgba(0, 0, 0, 0.03)',
  },
  addBuddyButton: {
    textTransform: 'none',
    borderRadius: 14,
    padding: theme.spacing(1.5, 3),
    fontSize: '0.9375rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #52b274 0%, #4a9e63 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(82, 178, 116, 0.35), 0 2px 4px rgba(82, 178, 116, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(82, 178, 116, 0.45), 0 4px 8px rgba(82, 178, 116, 0.3)',
      transform: 'translateY(-2px)',
      background: 'linear-gradient(135deg, #5fc085 0%, #52b274 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  conversationView: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.mode === 'dark' ? '#161616' : '#ffffff',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    minHeight: 200,
  },
  emptyStateIcon: {
    fontSize: 64,
    opacity: 0.3,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}))

// Component to display current user's status
function UserStatusDisplay() {
  const classes = useStyles()
  const userStatus = useSelector((state) => state.chat?.userStatus || 'online')
  const userStatusMessage = useSelector((state) => state.chat?.userStatusMessage || '')

  return (
    <div className={classes.userStatusCard}>
      <PresenceIcon status={userStatus} />
      {userStatusMessage ? (
        <Typography variant="caption" className={classes.userStatusText} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {userStatusMessage}
        </Typography>
      ) : (
        <Typography variant="caption" className={classes.userStatusText}>
          {userStatus === 'dnd' ? 'Do Not Disturb' : userStatus.charAt(0).toUpperCase() + userStatus.slice(1)}
        </Typography>
      )}
    </div>
  )
}

function ChatContent() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const selectedRoom = useSelector((state) => state.chat?.selectedRoom)
  const statusEditorOpen = useSelector((state) => state.chat?.statusEditorOpen || false)
  const buddyList = useSelector((state) => state.chat?.buddyList || [])
  const [search, setSearch] = useState('')
  const [addBuddyMode, setAddBuddyMode] = useState(false)
  const [activeTab, setActiveTab] = useState('chats')

  // Initialize presence heartbeat
  usePresenceHeartbeat()

  // Calculate counts for badges
  const onlineCount = Array.isArray(buddyList)
    ? buddyList.filter((b) => {
      const status = b?.presence?.status || 'offline'
      return status === 'online' || status === 'away' || status === 'dnd'
    }).length
    : 0

  // Helper function to safely extract error message as a string
  // This function never returns objects or null - only safe strings
  // Uses ultra-defensive programming to avoid any toString() calls on null
  const getSafeErrorMessage = (err) => {
    // First, check if err exists and is not null/undefined
    if (err === null || err === undefined) return 'Unknown error'

    // If it's already a string, return it directly
    try {
      if (typeof err === 'string') {
        return err || 'Unknown error'
      }
    } catch {
      // If even typeof check fails, return safe message
      return 'Error occurred (unable to parse error)'
    }

    // Try to extract message property with extreme caution
    let message = null
    try {
      // Use Object.prototype.hasOwnProperty to check if property exists
      if (Object.prototype.hasOwnProperty.call(err, 'message')) {
        const msg = err.message
        // Check if msg is not null/undefined and is a string
        if (msg !== null && msg !== undefined && typeof msg === 'string' && msg.length > 0) {
          message = msg
        }
      }
    } catch {
      // Silently ignore - we'll try other methods
    }

    if (message) return message

    // Try graphQLErrors array
    try {
      if (Object.prototype.hasOwnProperty.call(err, 'graphQLErrors')) {
        const gqlErrors = err.graphQLErrors
        if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
          const firstErr = gqlErrors[0]
          if (firstErr !== null && firstErr !== undefined) {
            if (Object.prototype.hasOwnProperty.call(firstErr, 'message')) {
              const msg = firstErr.message
              if (msg !== null && msg !== undefined && typeof msg === 'string' && msg.length > 0) {
                return msg
              }
            }
          }
        }
      }
    } catch {
      // Silently ignore
    }

    // Try networkError
    try {
      if (Object.prototype.hasOwnProperty.call(err, 'networkError')) {
        const netErr = err.networkError
        if (netErr !== null && netErr !== undefined) {
          if (typeof netErr === 'string') {
            return netErr
          }
          if (Object.prototype.hasOwnProperty.call(netErr, 'message')) {
            const msg = netErr.message
            if (msg !== null && msg !== undefined && typeof msg === 'string' && msg.length > 0) {
              return msg
            }
          }
        }
      }
    } catch {
      // Silently ignore
    }

    // Last resort: return a safe message
    return 'Error occurred (unable to extract details)'
  }

  // Get DM and Group counts
  // Note: Error handling is suppressed to prevent crashes from Apollo Client's error object serialization
  const { data: roomsData, loading: roomsLoading, error: roomsError } = useQuery(GET_CHAT_ROOMS, {
    fetchPolicy: 'cache-and-network',
    // Removed onError callback - it was causing crashes when Apollo Client tried to serialize error objects with null properties
    // The errorPolicy: 'all' will still allow the component to render with cached data
    errorPolicy: 'all', // Continue rendering even if there are errors
  })

  // Suppress error logging to prevent crashes from error object serialization
  // The error is handled gracefully by errorPolicy: 'all' which allows rendering with cached data
  // if (roomsError) {
  //   // Error logging suppressed to prevent "Cannot read properties of null (reading 'toString')" crashes
  // }

  const rooms = roomsData?.messageRooms || []
  const dmCount = rooms.filter((r) => r?.messageType === 'USER' && r?.users?.length === 2).length
  const groupCount = rooms.filter((r) => r?.messageType === 'POST' || (r?.messageType === 'USER' && r?.users?.length > 2)).length

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    // Exit add buddy mode when switching tabs
    if (addBuddyMode) {
      setAddBuddyMode(false)
      setSearch('')
    }
  }

  const handleAddBuddyModeChange = (mode) => {
    setAddBuddyMode(mode)
    if (!mode) {
      // Clear search when exiting add buddy mode
      setSearch('')
    }
  }

  const handleAddBuddyClick = () => {
    // Switch to buddies tab if not already there
    if (activeTab !== 'buddies') {
      setActiveTab('buddies')
    }
    setAddBuddyMode(true)
    // Focus the search input
    setTimeout(() => {
      const input = document.querySelector('input[aria-label="search users to add"]')
      if (input) input.focus()
    }, 100)
  }

  // Safety check - if selectedRoom is null or doesn't have room, show sidebar
  if (!selectedRoom || !selectedRoom?.room) {
    return (
      <Fade in timeout={300}>
        <div className={classes.root}>
          {/* Beautiful Gradient Header */}
          <div className={classes.sidebarHeader}>
            <div className={classes.headerContent}>
              <div className={classes.headerLeft}>
                <Typography className={classes.sidebarTitle}>Messages</Typography>
                <UserStatusDisplay />
              </div>
              <Tooltip title="Set Status" arrow>
                <IconButton
                  size="small"
                  onClick={() => dispatch(SET_STATUS_EDITOR_OPEN(true))}
                  className={classes.settingsButton}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Search Bar */}
          <div className={classes.searchContainer}>
            <ChatSearchInput
              setSearch={setSearch}
              addBuddyMode={addBuddyMode}
              onAddBuddyModeChange={handleAddBuddyModeChange}
            />
          </div>

          {/* Tabs */}
          <div className={classes.tabsContainer}>
            <ChatTabs
              value={activeTab}
              onChange={handleTabChange}
              dmCount={dmCount}
              groupCount={groupCount}
              onlineCount={onlineCount}
            />
          </div>

          {/* Add Buddy Button (only for buddies tab, hide when in add buddy mode) */}
          {activeTab === 'buddies' && !addBuddyMode && (
            <div className={classes.addBuddyContainer}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                className={classes.addBuddyButton}
                onClick={handleAddBuddyClick}
                fullWidth
              >
                Add New Buddy
              </Button>
            </div>
          )}

          {/* Content Area */}
          <div className={classes.contentArea}>
            {addBuddyMode ? (
              <UserSearchResults searchQuery={search} />
            ) : (
              <>
                {activeTab === 'chats' && <ChatList search={search} filterType="chats" />}
                {activeTab === 'groups' && <ChatList search={search} filterType="groups" />}
                {activeTab === 'buddies' && <BuddyListWithPresence search={search} />}
              </>
            )}
          </div>

          {/* Dialogs */}
          <StatusEditor
            open={statusEditorOpen}
            onClose={() => dispatch(SET_STATUS_EDITOR_OPEN(false))}
          />
        </div>
      </Fade>
    )
  }

  return (
    <Slide direction="left" in timeout={300}>
      <div className={classes.root}>
        <div className={classes.conversationView}>
          <MessageBox />
        </div>
      </div>
    </Slide>
  )
}

export default ChatContent
